import React, { useState, useEffect, useCallback } from 'react';
import AgentChat from '../components/AgentChat';
import SeoHead from '../components/SeoHead';
import AgentSelector from '../components/AgentSelector';


// shared styles for the side panel to keep the right column visually lighter
const sidePanelStyle = {
  flex: '0 0 360px',
  maxWidth: 420,
  background: '#FFF9F7',
  border: '1px solid #F3D3CB',
  borderRadius: 12,
  padding: 14,
  boxShadow: '0 10px 22px rgba(192, 54, 38, 0.08)',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
};

const sectionCardStyle = {
  background: '#fff',
  border: '1px solid #F0DFDB',
  borderRadius: 10,
  padding: 12,
  boxShadow: '0 6px 14px rgba(0,0,0,0.04)',
};

const sectionHeadingStyle = {
  fontWeight: 700,
  marginBottom: 6,
  color: '#C03626',
};

const sectionHintStyle = {
  fontSize: 13,
  color: '#5E514D',
  marginBottom: 10,
};

const uploadButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  cursor: 'pointer',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px dashed #C03626',
  background: '#FFF4F1',
  color: '#C03626',
  fontWeight: 600,
};

// Simple context file list component
const ContextFileList = ({ sessionId, refreshSignal, onRemove, contextType = 'company' }) => {
  const [files, setFiles] = useState([]);

  const fetchFiles = useCallback(async () => {
    if (!sessionId) {
      setFiles([]);
      return;
    }
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${apiBase}/api/upload/files?sessionId=${encodeURIComponent(sessionId)}&contextType=${encodeURIComponent(contextType)}`, { credentials: 'include' });
      const json = await res.json().catch(() => null);
      if (json?.files) setFiles(json.files);
    } catch (err) {
      console.error('Failed to list context files', err);
    }
  }, [sessionId, contextType]);

  useEffect(() => { fetchFiles(); }, [fetchFiles, refreshSignal]);

  const handleRemove = async (filename) => {
    if (!sessionId) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remove context file ${filename}? This cannot be undone for this session.`)) return;
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${apiBase}/api/upload/files?sessionId=${encodeURIComponent(sessionId)}&filename=${encodeURIComponent(filename)}&contextType=${encodeURIComponent(contextType)}`, { method: 'DELETE', credentials: 'include' });
      const json = await res.json();
      if (json?.ok) {
        if (onRemove) onRemove();
        fetchFiles();
      } else {
        alert('Failed to remove file');
      }
    } catch (err) {
      console.error('Remove failed', err);
      alert('Remove failed');
    }
  };

  if (!sessionId) return <div style={{ color: '#999' }}>Upload a file to create a session first.</div>;

  return (
    <div>
      {files.length === 0 && <div style={{ color: '#7A6B66' }}>No context files uploaded.</div>}
      {files.map((f) => (
        <div key={f.filename} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F0DFDB' }}>
          <div style={{ fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: '#2D1F1B' }}>{f.filename}</div>
            <div style={{ fontSize: 12, color: '#7A6B66' }}>{f.count} chunk(s)</div>
          </div>
          <div>
            <button onClick={() => handleRemove(f.filename)} style={{ padding: '6px 8px', borderRadius: 6, background: '#FFF7F5', border: '1px solid #E6C6BF', cursor: 'pointer', color: '#C03626' }}>Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
};

const Agents = () => {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('ragSessionId') || '');
  const [selectedModule, setSelectedModule] = useState('');
  const [preservedHistory, setPreservedHistory] = useState(null);
  const [filesRefresh, setFilesRefresh] = useState(0);
  const [uploading, setUploading] = useState({ company: false, practices: false });
  const [uploadLabels, setUploadLabels] = useState({ company: '', practices: '' });
  const setUploadState = (key, active, label = '') => {
    setUploading((prev) => ({ ...prev, [key]: active }));
    setUploadLabels((prev) => ({ ...prev, [key]: label }));
  };

  // (removed unused leftColRef/leftColWidth resize observer — kept markup simpler)

  const handleModuleChange = (moduleKey, { preserveHistory = false, history = null, autoAsk = null } = {}) => {
    console.log('[Agents] handleModuleChange()', { moduleKey, preserveHistory, hasHistory: !!history, autoAsk });
    if (preserveHistory && history) {
      setPreservedHistory({ module: moduleKey, messages: history, autoAsk });
    } else {
      setPreservedHistory(null);
    }
    setSelectedModule(moduleKey);
  };

  useEffect(() => {
    if (sessionId) localStorage.setItem('ragSessionId', sessionId);
  }, [sessionId]);

  const renderProgress = (key) => {
    if (!uploading[key]) return null;
    const label = uploadLabels[key] || 'Processing upload...';
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 12, color: '#7A6B66', marginBottom: 4 }}>{label}</div>
        <div style={{ position: 'relative', height: 8, borderRadius: 999, background: '#F4E1DB', overflow: 'hidden' }}>
          <div className="aice-upload-bar" style={{ position: 'absolute', inset: 0, width: '45%', background: 'linear-gradient(90deg, #C03626, #FF6F61)', animation: 'aiceUploadSlide 1s ease-in-out infinite' }} />
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '2rem' }}>
      <SeoHead
        title="AICE Agents | Specialiserede AI-agenter"
        description="Udforsk AICE's specialiserede AI-agenter til testcases, change management, idéfasen m.m. Upload filer og chat med agenterne."
        canonicalPath="/agents"
        robots="noindex,nofollow"
      />
      <style>{`
        @keyframes aiceUploadSlide {
          0% { transform: translateX(-20%); opacity: 0.6; }
          50% { transform: translateX(70%); opacity: 1; }
          100% { transform: translateX(130%); opacity: 0.6; }
        }
      `}</style>
      <div style={{ marginBottom: '1rem' }}>
        <AgentSelector />
      </div>
      <div>
        <h2 style={{ marginBottom: 6 }}>Agents</h2>
        <p style={{ marginTop: 0 }}>Chat with the customized agent below. You can upload files to provide context.</p>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ width: '100%', minWidth: 0 }}>
            {/* Module selection area laid out per design image */}
            {(() => {
              const modulesDef = {
                // use internal module keys for selection consistency
                top: { display: 'Project Management Planning', key: 'default' },
                row4: [
                  { display: 'PLC Idea generation', key: 'plcIdeaGeneration' },
                  { display: 'PLC Scoping', key: 'plcScopingAndPreliminaryAnalysis' },
                  { display: 'PLC Use case writer', key: 'plcRequirementsAndUserStories' },
                  { display: 'PLC Test case writer', key: 'testcases' },
                  { display: 'PLC Process documentation', key: 'plcProcessDocumentation' },
                ],
                after: [
                  { display: 'PLC Business case', key: 'plcBusinessCase' },
                  { display: 'PLC Change Management', key: 'plcChangeManagement' },
                  { display: 'PLC Risk Management', key: 'plcRiskManagement' },
                ],
              };

              const tileCommon = (enabled, selected) => ({
                padding: 8,
                borderRadius: 4,
                border: selected ? '2px solid #D32F2F' : '1px solid #E53935',
                background: selected ? '#FFF3F3' : (enabled ? '#fff' : '#f5f5f5'),
                color: enabled ? '#333' : '#999',
                cursor: enabled ? 'pointer' : 'default',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                whiteSpace: 'pre-wrap',
                fontSize: 13,
              });

              return (
                <div>
                  <div style={{ marginBottom: 8 }}>
                    {(() => {
                      const it = modulesDef.top;
                      const isSelected = it.key && it.key === selectedModule;
                      return (
                        <div style={{ ...tileCommon(!!it.key, isSelected), height: 48 }} onClick={() => it.key && handleModuleChange(it.key)}>
                          <div style={{ fontWeight: 600, fontSize: 16 }}>{it.display}</div>
                        </div>
                      );
                    })()}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 8 }}>
                    {modulesDef.row4.map((m) => {
                      const isSelected = m.key && m.key === selectedModule;
                      return (
                        <div key={m.display} style={{ ...tileCommon(!!m.key, isSelected), minHeight: 72 }} onClick={() => m.key && handleModuleChange(m.key)}>
                          <div style={{ fontSize: 13 }}>{m.display}</div>
                        </div>
                      );
                    })}
                  </div>

                  {modulesDef.after.map((m) => {
                    const isSelected = m.key && m.key === selectedModule;
                    return (
                      <div key={m.display} style={{ marginBottom: 8 }}>
                        <div style={{ ...tileCommon(!!m.key, isSelected), height: 48 }} onClick={() => m.key && handleModuleChange(m.key)}>
                          <div style={{ fontSize: 13 }}>{m.display}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {sessionId && <div style={{ marginBottom: '1rem' }}>Session id: {sessionId}</div>}

            {selectedModule ? (
              <AgentChat moduleName={selectedModule} sessionId={sessionId} setSessionId={setSessionId} preservedHistory={preservedHistory} onSwitchModule={(m, opts) => handleModuleChange(m, opts)} />
            ) : (
              <div style={{ color: '#666' }}>Choose a module to start the agent.</div>
            )}
          </div>
        </div>

        <div style={sidePanelStyle}>
          <div style={sectionCardStyle}>
            <div style={sectionHeadingStyle}>Context files (company)</div>
            <div style={sectionHintStyle}>Upload op til 10 PDF’er (max 10MB hver). Filer bruges som session-kontekst til spørgsmål.</div>

            <input id="context-file-input" type="file" accept="application/pdf,.pdf" multiple style={{ display: 'none' }} onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              const tooLarge = files.find((f) => f.size > 10 * 1024 * 1024);
              if (tooLarge) {
                alert(`File ${tooLarge.name} is too large. Max 10MB pr. fil`);
                e.target.value = '';
                return;
              }

              let roomLeft = 10;
              try {
                const apiBase = process.env.REACT_APP_API_URL || '';
                const listRes = await fetch(`${apiBase}/api/upload/files?sessionId=${encodeURIComponent(sessionId || '')}&contextType=company`, { credentials: 'include' });
                const listJson = await listRes.json().catch(() => null);
                const currentCount = Array.isArray(listJson?.files) ? listJson.files.length : 0;
                roomLeft = Math.max(0, 10 - currentCount);
                if (roomLeft <= 0) {
                  alert('Max 10 context files reached');
                  e.target.value = '';
                  return;
                }
              } catch (err) {
                // ignore listing errors
              }

              const filesToUpload = files.slice(0, roomLeft);
              if (filesToUpload.length < files.length) {
                alert(`Kun ${filesToUpload.length} fil(er) uploades – grænsen er 10.`);
              }

              try {
                setUploadState('company', true, `Uploader ${filesToUpload.length} fil(er)...`);
                const apiBase = process.env.REACT_APP_API_URL || '';

                // upload sequentially to keep sessionId consistent
                for (const file of filesToUpload) {
                  setUploadState('company', true, `Behandler ${file.name}...`);
                  const form = new FormData();
                  if (sessionId) form.append('sessionId', sessionId);
                  form.append('file', file);
                  form.append('contextType', 'company');
                  const res = await fetch(`${apiBase}/api/upload`, { method: 'POST', credentials: 'include', body: form });
                  const json = await res.json();
                  if (json?.sessionId) setSessionId(json.sessionId);
                }
                setFilesRefresh(Date.now());
              } catch (err) {
                console.error('Context upload failed', err);
                alert('Upload failed');
              } finally {
                e.target.value = '';
                setUploadState('company', false, '');
              }
            }} />

            <label htmlFor="context-file-input" style={uploadButtonStyle}>
              + Upload context PDF
            </label>

            {renderProgress('company')}

            <div style={{ marginTop: 12 }}>
              <ContextFileList sessionId={sessionId} refreshSignal={filesRefresh} onRemove={() => setFilesRefresh(Date.now())} />
            </div>
          </div>

          <div style={sectionCardStyle}>
            <div style={sectionHeadingStyle}>Company methods & strategies</div>
            <div style={sectionHintStyle}>Upload metode/strategi-PDF’er (max 10MB hver). Opbevares separat fra session-kontekst.</div>

            <input id="practices-file-input" type="file" accept="application/pdf,.pdf" multiple style={{ display: 'none' }} onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              if (!files.length) return;
              const tooLarge = files.find((f) => f.size > 10 * 1024 * 1024);
              if (tooLarge) {
                alert(`File ${tooLarge.name} is too large. Max 10MB pr. fil`);
                e.target.value = '';
                return;
              }
              let roomLeft = 50;
              try {
                const apiBase = process.env.REACT_APP_API_URL || '';
                const listRes = await fetch(`${apiBase}/api/upload/files?sessionId=${encodeURIComponent(sessionId || '')}&contextType=practices`, { credentials: 'include' });
                const listJson = await listRes.json().catch(() => null);
                const currentCount = Array.isArray(listJson?.files) ? listJson.files.length : 0;
                roomLeft = Math.max(0, 50 - currentCount);
                if (roomLeft <= 0) {
                  alert('Max 50 practices files reached');
                  e.target.value = '';
                  return;
                }
              } catch (err) {
                // ignore listing errors
              }

              const filesToUpload = files.slice(0, roomLeft);
              if (filesToUpload.length < files.length) {
                alert(`Kun ${filesToUpload.length} fil(er) uploades – grænsen er 50.`);
              }

              try {
                setUploadState('practices', true, `Uploader ${filesToUpload.length} fil(er)...`);
                const apiBase = process.env.REACT_APP_API_URL || '';

                for (const file of filesToUpload) {
                  setUploadState('practices', true, `Behandler ${file.name}...`);
                  const form = new FormData();
                  if (sessionId) form.append('sessionId', sessionId);
                  form.append('file', file);
                  form.append('contextType', 'practices');
                  const res = await fetch(`${apiBase}/api/upload`, { method: 'POST', credentials: 'include', body: form });
                  const json = await res.json();
                  if (json?.sessionId) setSessionId(json.sessionId);
                }
                setFilesRefresh(Date.now());
              } catch (err) {
                console.error('Practices upload failed', err);
                alert('Upload failed');
              } finally {
                e.target.value = '';
                setUploadState('practices', false, '');
              }
            }} />

            <label htmlFor="practices-file-input" style={uploadButtonStyle}>
              + Upload methods/strategy PDF
            </label>

            {renderProgress('practices')}

            <div style={{ marginTop: 12 }}>
              <ContextFileList sessionId={sessionId} refreshSignal={filesRefresh} onRemove={() => setFilesRefresh(Date.now())} contextType="practices" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Agents;
